import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ShieldCheck, KeyRound, Copy, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import DemoLinksSection from "@/components/DemoLinksSection";

export default function AdminsPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [resetResult, setResetResult] = useState(null);

  const load = () => api.get("/users/admins").then((r) => setAdmins(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", { ...form, role: "admin" });
      toast.success("Administrador creado");
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPassword = async (a) => {
    if (!window.confirm(`¿Generar nueva contraseña temporal para ${a.name}?`)) return;
    try {
      const { data } = await api.post(`/users/admins/${a.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar el administrador ${a.name}? Esta acción es irreversible.`)) return;
    try {
      await api.delete(`/users/admins/${a.id}`);
      toast.success("Administrador eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyTemp = () => {
    if (!resetResult) return;
    navigator.clipboard?.writeText(resetResult.temp_password);
    toast.success("Contraseña copiada");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>Administradores</h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestiona el equipo directivo del club. Los admins ven todos los datos del club.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="new-admin-button">
              <Plus className="h-4 w-4 mr-2" /> Nuevo administrador
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Registrar administrador</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" />
              </div>
              <div>
                <Label>Contraseña inicial</Label>
                <Input required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" />
                <p className="text-xs text-slate-500 mt-1">El administrador podrá cambiarla más adelante.</p>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="admin-save-button">
                  Crear administrador
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {admins.length === 0 && <div className="text-slate-500 text-sm col-span-full">Cargando...</div>}
        {admins.map((a) => {
          const isMe = a.id === user?.id;
          return (
            <Card key={a.id} className="border-slate-200 shadow-sm" data-testid={`admin-card-${a.id}`}>
              <CardContent className="p-5 flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-purple-100 text-purple-800 grid place-items-center">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-black text-lg" style={{ fontFamily: "Nunito" }}>{a.name}</div>
                    {isMe && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">
                        Tú
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 truncate">{a.email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">
                    Director deportivo
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetPassword(a)}
                      data-testid={`reset-admin-${a.id}`}
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-2" /> Resetear contraseña
                    </Button>
                    {!isMe && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remove(a)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        data-testid={`delete-admin-${a.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Eliminar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!resetResult} onOpenChange={(v) => !v && setResetResult(null)}>
        <DialogContent data-testid="admin-reset-result">
          <DialogHeader>
            <DialogTitle>Contraseña temporal generada</DialogTitle>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Pásale estos datos al administrador.</p>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Email</div>
                <div className="font-mono text-sm font-bold">{resetResult.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 pt-2">Contraseña temporal</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-base font-bold text-purple-800 bg-white border border-slate-200 rounded px-3 py-2">
                    {resetResult.temp_password}
                  </code>
                  <Button size="icon" variant="outline" onClick={copyTemp}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setResetResult(null)} className="bg-purple-600 hover:bg-purple-700 text-white">
                  Entendido
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ViewersSection />
      <DemoLinksSection />
    </div>
  );
}

function ViewersSection() {
  const [viewers, setViewers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [resetResult, setResetResult] = useState(null);

  const load = () => {
    api.get("/users/viewers")
      .then((r) => setViewers(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/users/viewers", form);
      toast.success("Cuenta de observador creada");
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPwd = async (v) => {
    try {
      const { data } = await api.post(`/users/viewers/${v.id}/reset-password`);
      setResetResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`¿Eliminar la cuenta de "${v.name}"?`)) return;
    try {
      await api.delete(`/users/viewers/${v.id}`);
      toast.success("Observador eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  return (
    <div className="mt-8" data-testid="viewers-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-purple-700" />
          <h2 className="text-xl font-black tracking-tight" style={{ fontFamily: "Nunito" }}>
            Cuentas de solo lectura
          </h2>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-purple-700 hover:bg-purple-800 text-white"
          data-testid="viewer-new-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Nueva cuenta observador
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Comparte estas credenciales con presidencia, patrocinadores u observadores externos.
        Podrán ver toda la información del club (equipos, jugadores, partidos, KPIs, PDFs) pero
        <b> no podrán crear, editar ni borrar nada</b>.
      </p>

      {viewers.length === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-8 text-center text-slate-400 italic text-sm">
            Aún no hay cuentas de observador. Crea la primera con el botón de arriba.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {viewers.map((v) => (
            <Card key={v.id} className="border-slate-200">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{v.name}</div>
                  <div className="text-xs text-slate-500 truncate">{v.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resetPwd(v)}
                    data-testid={`viewer-reset-${v.id}`}
                  >
                    <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Resetear contraseña
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-rose-700 border-rose-200 hover:bg-rose-50"
                    onClick={() => remove(v)}
                    data-testid={`viewer-del-${v.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="viewer-create-dialog">
          <DialogHeader>
            <DialogTitle>Nueva cuenta de solo lectura</DialogTitle>
            <DialogDescription>
              Crea una cuenta para compartir con presidencia, patrocinadores u observadores externos. Solo podrán leer datos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Junta directiva"
                data-testid="viewer-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="observador@ejemplo.com"
                data-testid="viewer-email-input"
              />
            </div>
            <div>
              <Label>Contraseña inicial</Label>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Al menos 6 caracteres"
                data-testid="viewer-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              onClick={submit}
              className="bg-purple-700 hover:bg-purple-800 text-white"
              data-testid="viewer-submit"
            >
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva contraseña generada</DialogTitle>
            <DialogDescription>
              Contraseña temporal única. Copia y comparte con la persona; después no volverá a mostrarse.
            </DialogDescription>
          </DialogHeader>
          {resetResult && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                Compártela con <b>{resetResult.email}</b>. La contraseña antigua queda invalidada.
              </div>
              <div className="p-3 bg-slate-100 rounded font-mono text-sm break-all">
                {resetResult.temp_password}
              </div>
              <Button
                variant="outline"
                onClick={() => copy(resetResult.temp_password)}
                data-testid="viewer-copy-pwd"
              >
                <Copy className="h-4 w-4 mr-1.5" /> Copiar contraseña
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
